"use client";

import { InlineInfoBox } from "staysure-component-library";

import RichText from "@/components/atoms/rich-text/rich-text";
import {
  NOT_ELIGIBLE,
  REJECTED,
  WITHDRAWAL_REQUESTED,
  WITHDRAWN,
  ELIGIBLE,
} from "@/config/app-constant";
import { useWithdrawal } from "@/hooks/withdrawal/use-withdrawal";
import { getWithdrawalStatusConfig } from "@/utils/withdrawal/withdrawal-eligibility-status.utils";

import Styles from "./policy-details.module.css";

interface PolicyDetailsLabels {
  organiser?: string;
  policyNumber?: string;
  coverType?: string;
  coverLevel?: string;
  destination?: string;
  coverDates?: string;
  travellers?: string;
}

interface PolicyDetailsProps {
  sectionTitle?: string;
  labels?: PolicyDetailsLabels;
  organiser: string;
  policyNumber: string;
  coverType: string;
  coverLevel: string;
  destination: string;
  coverDates: string;
  travellers: string;
  infoBoxRejectedRichText?: string;
  infoBoxNotEligibleRichText?: string;
  eligibleBadgeText?: string;
  notEligibleBadgeText?: string;
  rejectedBadgeText?: string;
  withdrawnBadgeText?: string;
  requestedBadgeText?: string;
}

export default function PolicyDetails({
  sectionTitle,
  labels,
  organiser,
  policyNumber,
  coverType,
  coverLevel,
  destination,
  coverDates,
  travellers,
  infoBoxRejectedRichText,
  infoBoxNotEligibleRichText,
  eligibleBadgeText,
  notEligibleBadgeText,
  rejectedBadgeText,
  withdrawnBadgeText,
  requestedBadgeText,
}: Readonly<PolicyDetailsProps>) {
  const { policyData } = useWithdrawal();

  const eligibilityStatus =
    policyData?.withdrawalData.withdrawalEligibilityStatus;
  const statusConfig = getWithdrawalStatusConfig(eligibilityStatus);

  // Get formatted date from policyData (already formatted in transformWithdrawalData)
  const formattedModifiedDate = policyData?.withdrawalData.modifiedDate;

  // Get badge text from Magnolia or fallback to config
  const getBadgeText = () => {
    switch (eligibilityStatus) {
      case ELIGIBLE:
        return eligibleBadgeText || statusConfig?.text;
      case NOT_ELIGIBLE:
        return notEligibleBadgeText || statusConfig?.text;
      case REJECTED:
        return rejectedBadgeText || statusConfig?.text;
      case WITHDRAWN:
        return withdrawnBadgeText || statusConfig?.text;
      case WITHDRAWAL_REQUESTED:
        return requestedBadgeText || statusConfig?.text;
      default:
        return statusConfig?.text;
    }
  };

  return (
    <div className="mb-spacing-l">
      <div className="flex flex-col md:flex-row md:items-start md:justify-between mb-spacing-s gap-spacing-xs">
        {sectionTitle && (
          <h2 className="font-header-font text-l text-text-title font-normal leading-[28px]">
            {sectionTitle}
          </h2>
        )}

        <div className="flex flex-col gap-spacing-xs items-start md:items-end">
          {statusConfig && (
            <div
              className="flex items-center gap-spacing-xxs rounded-radius-full px-spacing-xs py-spacing-xxs w-fit"
              style={{
                backgroundColor: statusConfig.bgColor,
              }}
              data-testid="eligibility-status-badge"
            >
              {statusConfig.icon}
              <span
                className="font-body-font text-2xs font-semibold leading-6"
                style={{
                  color: statusConfig.textColor,
                }}
              >
                {getBadgeText()}
              </span>
            </div>
          )}

          {eligibilityStatus === WITHDRAWAL_REQUESTED &&
            formattedModifiedDate && (
              <p className="font-body-font text-4xs font-semibold text-text-functional-subtle leading-4 self-stretch md:text-right">
                Requested on {formattedModifiedDate}
              </p>
            )}

          {eligibilityStatus === WITHDRAWN && formattedModifiedDate && (
            <p className="font-body-font text-4xs font-semibold text-text-functional-subtle leading-4 self-stretch md:text-right">
              Request approved on {formattedModifiedDate}
            </p>
          )}
        </div>
      </div>

      {eligibilityStatus === REJECTED && infoBoxRejectedRichText && (
        <InlineInfoBox
          tabIndex={-1}
          className={`pl-[22px] pr-spacing-m py-spacing-xs w-full font-body-font font-normal text-3xs text-text-functional leading-[21px] rounded-lg border !border-border-critical-primary mb-spacing-s bg-surface-critical ${Styles["policy-details"]}`}
          content={<RichText richText={infoBoxRejectedRichText} />}
        />
      )}

      {eligibilityStatus === NOT_ELIGIBLE && infoBoxNotEligibleRichText && (
        <InlineInfoBox
          tabIndex={-1}
          className={`pl-[22px] pr-spacing-m py-spacing-xs w-full font-body-font font-normal text-3xs text-text-functional leading-[21px] rounded-lg border !border-[#B35000] mb-spacing-s bg-surface-warning ${Styles["policy-details"]}`}
          content={<RichText richText={infoBoxNotEligibleRichText} />}
        />
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-spacing-l gap-y-spacing-xs">
        <div>
          {labels?.organiser && (
            <p className="font-body-font text-2xs font-normal text-text-functional-subtle leading-6">
              {labels.organiser}
            </p>
          )}
          <p className="font-body-font text-2xs font-bold text-[#136A9C] leading-6">
            {organiser}
          </p>
        </div>

        <div>
          {labels?.policyNumber && (
            <p className="font-body-font text-2xs font-normal text-text-functional-subtle leading-6">
              {labels.policyNumber}
            </p>
          )}
          <p className="font-body-font text-2xs font-bold text-[#136A9C] leading-6">
            {policyNumber}
          </p>
        </div>

        <div>
          {labels?.coverType && (
            <p className="font-body-font text-2xs font-normal text-text-functional-subtle leading-6">
              {labels.coverType}
            </p>
          )}
          <p className="font-body-font text-2xs font-bold text-[#136A9C] leading-6">
            {coverType}
          </p>
        </div>

        <div>
          {labels?.coverLevel && (
            <p className="font-body-font text-2xs font-normal text-text-functional-subtle leading-6">
              {labels.coverLevel}
            </p>
          )}
          <p className="font-body-font text-2xs font-bold text-[#136A9C] leading-6">
            {coverLevel}
          </p>
        </div>

        <div>
          {labels?.destination && (
            <p className="font-body-font text-2xs font-normal text-text-functional-subtle leading-6">
              {labels.destination}
            </p>
          )}
          <p
            className="font-body-font text-2xs font-bold text-[#136A9C] leading-6"
            title={destination}
          >
            {destination}
          </p>
        </div>

        <div>
          {labels?.coverDates && (
            <p className="font-body-font text-2xs font-normal text-text-functional-subtle leading-6">
              {labels.coverDates}
            </p>
          )}
          <p className="font-body-font text-2xs font-bold text-[#136A9C] leading-6">
            {coverDates}
          </p>
        </div>

        <div>
          {labels?.travellers && (
            <p className="font-body-font text-2xs font-normal text-text-functional-subtle leading-6">
              {labels.travellers}
            </p>
          )}
          <p className="font-body-font text-2xs font-bold text-[#136A9C] leading-6">
            {travellers}
          </p>
        </div>
      </div>
    </div>
  );
}
